/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Voice Catalog Card Component (Task 10.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Play,
  Pause,
  Star,
  Coins,
  Check,
  Mic,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { VoiceItem, useVoiceCatalogStore } from '../../stores/useVoiceCatalogStore';
import { MiniWaveform } from './MiniWaveform';

interface VoiceCardProps {
  voice: VoiceItem;
  onSelectVoice?: (voice: VoiceItem) => void;
  isSelected?: boolean;
}

export const VoiceCard: React.FC<VoiceCardProps> = ({
  voice,
  onSelectVoice,
  isSelected = false,
}) => {
  const { t } = useTranslation();
  const {
    activeAuditionVoiceId,
    isPlaying,
    playbackProgress,
    playAudition,
    pauseAudition,
    toggleFavorite,
  } = useVoiceCatalogStore();

  const isCurrentAudition = activeAuditionVoiceId === voice.id;
  const isCurrentlyPlaying = isCurrentAudition && isPlaying;

  const handlePlayToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCurrentlyPlaying) {
      pauseAudition();
    } else {
      playAudition(voice.id);
    }
  };

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(voice.id);
  };

  const getProviderBadge = () => {
    switch (voice.provider) {
      case 'openai':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
            OpenAI
          </span>
        );
      case 'elevenlabs':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            ElevenLabs
          </span>
        );
      case 'google':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
            Google Cloud
          </span>
        );
      case 'azure':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            Azure Speech
          </span>
        );
      case 'custom_clone':
      default:
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
            <Mic className="size-2.5" />
            Cloned Voice
          </span>
        );
    }
  };

  return (
    <div
      onClick={() => onSelectVoice?.(voice)}
      className={`group relative p-4 rounded-xl border transition-all flex flex-col justify-between space-y-3 cursor-pointer ${
        isSelected
          ? 'bg-primary/5 border-primary ring-1 ring-primary/40 shadow-sm'
          : isCurrentlyPlaying
          ? 'bg-surface-elevated/80 border-primary/60 shadow-md'
          : 'bg-surface border-surface-border hover:border-surface-border/80 hover:shadow-xs'
      }`}
    >
      {/* Top Header: Provider, Gender/Age, Favorite */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {getProviderBadge()}
          {voice.badge && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-surface-elevated text-foreground border border-surface-border">
              <Sparkles className="size-2.5 text-primary" />
              {voice.badge}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleFavoriteToggle}
          title={voice.isFavorite ? t('voice.unfavorite') : t('voice.favorite')}
          className={`p-1 rounded-lg transition-colors ${
            voice.isFavorite
              ? 'text-amber-500 hover:text-amber-600'
              : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
          }`}
        >
          <Star
            className={`size-4 ${voice.isFavorite ? 'fill-amber-500' : ''}`}
          />
        </button>
      </div>

      {/* Voice Name, Tone & Language */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-bold font-heading text-foreground group-hover:text-primary transition-colors truncate">
            {voice.name}
          </h3>
          <span className="text-[11px] font-medium text-foreground-muted shrink-0 capitalize">
            {voice.gender} &bull; {voice.ageGroup}
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
          <span className="font-semibold text-foreground truncate">
            {voice.languageLabel}
          </span>
          <span>&bull;</span>
          <span className="truncate">{voice.accentRegion}</span>
        </div>

        <p className="text-[11px] text-foreground-muted line-clamp-2 mt-1 leading-relaxed">
          {voice.description}
        </p>
      </div>

      {/* Sample Script Snippet Quote */}
      <div className="p-2 rounded-lg bg-surface-elevated/40 border border-surface-border text-[11px] text-foreground italic line-clamp-2">
        &ldquo;{voice.sampleScript}&rdquo;
      </div>

      {/* Audition Bar & Playback */}
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-surface-border">
        {/* Play/Pause Button */}
        <button
          type="button"
          onClick={handlePlayToggle}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            isCurrentlyPlaying
              ? 'bg-primary text-primary-fg shadow-xs scale-102'
              : 'bg-surface-elevated text-foreground hover:bg-primary hover:text-primary-fg'
          }`}
        >
          {isCurrentlyPlaying ? (
            <>
              <Pause className="size-3.5" />
              <span>{t('voice.pauseAudition')}</span>
            </>
          ) : (
            <>
              <Play className="size-3.5 fill-current" />
              <span>{t('voice.auditionSample')}</span>
            </>
          )}
        </button>

        {/* Mini Waveform Visualizer */}
        <div className="flex-1 min-w-[70px]">
          <MiniWaveform
            isPlaying={isCurrentlyPlaying}
            progress={isCurrentAudition ? playbackProgress : 0}
            barCount={14}
          />
        </div>

        {/* Credit Rate Cost Badge */}
        <div
          title={`${voice.creditRatePer100Chars} credits per 100 characters`}
          className="flex items-center gap-1 text-[11px] font-mono font-semibold px-2 py-1 rounded-md bg-surface-elevated text-foreground-muted shrink-0"
        >
          <Coins className="size-3 text-amber-500" />
          <span>{voice.creditRatePer100Chars} cr</span>
        </div>
      </div>

      {/* Select Voice Indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 size-4 rounded-full bg-primary text-primary-fg flex items-center justify-center">
          <Check className="size-2.5 stroke-[3]" />
        </div>
      )}
    </div>
  );
};
